import { useMemo } from "react";
import { DateTime } from "luxon";
import { SBBox, SBChip, SBIcon, SBTooltip } from "modules/Commons";
import { SBTableColumn } from "modules/Commons/SBDataDisplays/SBTable/models/TableColumn";
import { TableCellType } from "modules/Commons/SBDataDisplays/SBTable/SBTableCells/tableCellType";
import { useTranslation } from "react-i18next";

const PercentageNoDecimalFormatter = (value: number) => {
	if (value === undefined || value === null) return "-";
	return `${(value * 100).systemFormat()}%`;
};

const DecimalFormatter = (value: number) => {
	if (value === undefined || value === null) return "0";
	return `${value.systemFormat({ maximumFractionDigits: 0 })}`;
};

const NaturalFormatter = (value: number) => {
	if (value === undefined || value === null) return "0";
	var v = value.toString().naturalNumber();
	parseInt(v);
	return v;
}

const DateFormatter = (dateFormat: string) => (value: string) => {
	if (!value) return "-";
	var date = DateTime.fromISO(value);
	if (date.isValid) return date.toFormat(dateFormat);
	return "-";
};

export const useHeaders = (onOpenInventory?: (p: any, s: any) => void) => {
	const { t } = useTranslation();
	const headers: SBTableColumn<any>[] = useMemo(() => [
		{
			id: () => "Source",
			name: t("source"),
			hide: true,
			headers: [
				{
					id: (e: any) => e.source?.clientID ?? "",
					key: "sourceStoreCode",
					tableHide: true,
					name: t("sourceStoreCode"),
					align: "left",
					disabled: true,
					hide: true,
					noWrap: true,
					child: {
						id: (e: any) => e.source?.clientID,
						style: {
							color: "transparent",
							userSelect: "none",
						},
					},
				},
				{
					id: (e: any) => e.source?.name ?? "",
					key: "sourceStoreNAme",
					tableHide: true,
					name: t("sourceStoreName"),
					color: () => "#000000",
					align: "left",
					disabled: true,
					hide: true,
					noWrap: true,
					style: {
						fontWeight: "bold",
					},
					child: {
						id: (e: any) => e.source?.clientID,
						style: {
							color: "transparent",
							userSelect: "none",
						},
					},
				},
			],
		},
		{
			id: () => "Product",
			name: t("product"),
			headers: [
				{
					id: (e: any) => {
						if (!onOpenInventory) return null;
						return (
							<div
								style={{ cursor: "pointer" }}
								onClick={() =>
									onOpenInventory(e?.inventory?.id, null)
								}
							>
								<SBIcon
									icon="inventory"
									hoverFillColor="primary"
								/>
							</div>
						);
					},
					align: "center",
					csvHide: true,
					width: 1,
					key: "inventory",
					name: "",
					fixed: true,
					disableClick: true,
				},
				{
					id: () => "",
					align: "center",
					csvHide: true,
					width: 1,
					key: "hasOrder",
					name: "",
					tip: "hasOrderTip",
					child: {
						id: (e: any) =>
							e.hasOrder ? (
								<SBTooltip title={t("hasOrder")}>
									<SBIcon icon="clipboard" />
								</SBTooltip>
							) : "",
						align: "center",
						key: "hasOrderChild",
					},
				},
				{
					id: (e: any) => e.product?.clientID ?? e.inventory?.product?.clientID ?? "",
					key: "productID",
					name: t("code"),
					align: "left",
					noWrap: true,
					child: {
						id: (e: any) => e.product?.clientID ?? e.inventory?.product?.clientID,
						style: {
							color: "transparent",
							userSelect: "none",
						},
					},
				},
				{
					id: (e: any) => e.product?.name ?? e.inventory?.product?.name ?? "",
					key: "productName",
					name: t("product"),
					align: "left",
					noWrap: true,
					child: {
						id: (e: any) => e.product?.name ?? e.inventory?.product?.name,
						style: {
							color: "transparent",
							userSelect: "none",
						},
					},
				},
			],
		},
		{
			id: () => "Store",
			name: t("store"),
			headers: [
				{
					id: (e: any) =>
						e.store?.clientID ?? e.inventory?.store?.clientID ?? "",
					key: "id",
					name: t("code"),
					align: "left",
					noWrap: true,
				},
				{
					id: (e: any) => e.store?.name ?? e.inventory?.store?.name ?? "",
					key: "storeName",
					name: t("store"),
					align: "left",
					noWrap: true,
				},
				{
					id: (e: any) =>
						e.plans?.length ? (
							<SBChip
								color="primary"
								fullWidth={false}
								label={
									<SBBox display="flex" alignItems="center">
										<SBBox mb={0.3} mr={0.3}>
											<SBIcon
												icon="home"
												size="11"
												color="white"
											/>
										</SBBox>
										{e.plans?.length}
									</SBBox>
								}
							/>
						) : null,
					csvHide: true,
					key: "statusIcon",
					name: t(""),
					align: "left",
					noWrap: true,
					child: {
						id: (e: any) => e.type ? (
							<SBChip
								color="gray6"
								fullWidth={false}
								label={<SBBox>{e.type}</SBBox>}
							/>
						) : null,
						key: "statusType",
						name: t("type"),
						align: "left",
						noWrap: true,
					},
				},
			],
		},
		{
			id: () => "Category",
			name: t("category"),
			headers: [
				{
					id: (e: any) => e.product?.category?.clientID ?? e.inventory?.product?.category?.clientID,
					key: "categoryCode",
					name: t("code"),
					align: "left",
					noWrap: true,
					tip: "categoryClientIDTip",
				},
				{
					id: (e: any) => e.product?.category?.name ?? e.inventory?.product?.category?.name,
					key: "categoryName",
					name: t("name"),
					align: "left",
					noWrap: true,
					tip: "categoryNameTip",
				},
			],
		},
		{
			id: () => "Distribution",
			name: t("distribution"),
			headers: [
				{
					id: (e: any) => e.initial?.excess,
					key: "available",
					name: t("available"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					child: {
						id: (e: any) => "-",
						key: "available",
						name: t("available"),
						align: "right",
					},
				},
				{
					id: (c: any) => c.netNeed,
					key: "needed",
					csvName: `${t("needed")} (${t("unit")})`,
					name: t("needed"),
					align: "right",
					noWrap: true,
					type: TableCellType.Number,
					formatter: DecimalFormatter,
				},
				{
					id: () => "-",
					key: "modifier",
					csvName: `${t("modifier")} (${t("unit")})`,
					name: t("modifier"),
					disabled: true,
					align: "right",
					noWrap: true,
					type: TableCellType.Number,
					child: {
						id: (c: any) => c.modifier || 0,
						editable: true,
						setter: (e: any, value: any) => (e.modifier = +value),
						align: "right",
						noWrap: true,
					},
				},
				{
					id: (e: any) => (e.plans || []).reduce((a: number, b: any) => a + Math.max(0, (+b.amount) + (+b.modifier || 0)), 0),
					key: "qty",
					csvName: `${t("qty")} (${t("unit")})`,
					name: t("qty"),
					style: {
						minWidth: 100,
					},
					align: "right",
					noWrap: true,
					type: TableCellType.Number,
					formatter: DecimalFormatter,
					child: {
						id: (c: any) => Math.max(0, (+c.amount) + (+c.modifier || 0)),
						formatter: DecimalFormatter,
						align: "right",
						noWrap: true,
					},
				},
				{
					id: (c: any) => {
						let currentStatus = c.status
							?.map((s: string) => t(s.trimStart()))
							.join(", ") || "-";

						if (c.plans && (c.inventory || c.initial)) {
							const onHandLimit = c.initial?.position?.onHand ?? c.inventory?.onHand ?? 0;
							const totalDistribution = c.plans.reduce(
								(a: number, b: any) => a + Math.max(0, (+b.amount) + (+b.modifier || 0)),
								0
							);
							if (totalDistribution > onHandLimit) {
								const warning = t("modifierExceedsAvailability");
								currentStatus = currentStatus !== "-" ? `${currentStatus}, ${warning}` : warning;
							}
						}

						return currentStatus;
					},
					key: "status",
					name: t("status"),
					align: "left",
					type: TableCellType.String,
					style: {
						whiteSpace: "nowrap",
					},
				},
			],
		},
		{
			id: () => "Cycle",
			name: t("cycle"),
			headers: [
				{
					id: (e: any) => e.cycle?.request ?? "-",
					key: "request",
					name: t("request"),
					align: "right",
					noWrap: true,
					formatter: DateFormatter(t("dateFormat")),
				},
				{
					id: (e: any) => e.cycle?.delivery ?? "-",
					key: "delivery",
					name: t("delivery"),
					align: "right",
					noWrap: true,
					formatter: DateFormatter(t("dateFormat")),
				},
				{
					id: (e: any) => e.cycle?.coverage ?? "-",
					key: "coverage",
					name: t("coverage"),
					align: "right",
					noWrap: true,
					formatter: DateFormatter(t("dateFormat")),
				},
				{
					id: (e: any) => e.cycle?.nextReview ?? "-",
					key: "nextReview",
					name: t("nextReview"),
					align: "right",
					noWrap: true,
					formatter: DateFormatter(t("dateFormat")),
				},
				{
					id: (e: any) => e.cycle?.coveredDays,
					key: "daysBetweenCount",
					name: t("days"),
					align: "right",
					noWrap: true,
					formatter: NaturalFormatter,
					tip: "cycleDaysBetweenCountTip",
				},
				{
					id: (e: any) => e.forecast?.expected ?? e.forecast,
					key: "forecast",
					name: t("forecast"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					tip: "cycleForecastTip",
				},
				{
					id: (e: any) => e.deviation,
					key: "deviation",
					name: t("deviation"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					tip: "cycleDeviationTip",
				},
			],
		},
		{
			id: () => "Config",
			name: t("config"),
			headers: [
				{
					id: (e: any) => e?.minCSL,
					key: "scarcityCSL",
					name: t("scarcity"),
					align: "right",
					noWrap: true,
					formatter: PercentageNoDecimalFormatter,
				},
				{
					id: (e: any) => e?.csl,
					key: "targetCSL",
					name: t("target"),
					align: "right",
					noWrap: true,
					formatter: PercentageNoDecimalFormatter,
				},
				{
					id: (e: any) => e?.maxCSL,
					key: "excessCSL",
					name: t("excess"),
					align: "right",
					noWrap: true,
					formatter: PercentageNoDecimalFormatter,
				},
			],
		},
		{
			id: () => "initialPosition",
			name: t("initialPosition"),
			headers: [
				{
					id: (e: any) => e.initial?.position?.onHand,
					key: "initialOnHand",
					name: t("onHand"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.initial?.position?.onOrder,
					key: "initialOnOrder",
					name: t("onOrder"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.initial?.position?.reserved,
					key: "pending",
					name: t("commons.pending"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					tip: "tips.pending",
				},
				{
					id: (e: any) => e.initial?.position?.position,
					key: "position",
					name: t("position"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.initial?.csl,
					key: "csl",
					name: t("csl"),
					align: "right",
					noWrap: true,
					formatter: PercentageNoDecimalFormatter,
				},
				{
					id: (e: any) => e.initial?.doh,
					key: "doh",
					name: t("DOH"),
					align: "right",
					noWrap: true,
					formatter: NaturalFormatter,
				},
				{
					id: (e: any) => e.initial?.expectedSales,
					key: "expectedSales",
					name: t("expectedSales"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.rupture,
					key: "rupture",
					name: t("rupture"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.plans?.reduce((a: number, b: any) => a + (b.initial?.eus || 0), 0),
					key: "risk",
					name: t("risk"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					child: {
						id: (e: any) => e.initial?.eus,
						key: "riskChild",
						name: t("risk"),
						align: "right",
						noWrap: true,
						formatter: DecimalFormatter,
					},
				},
			],
		},
		{
			id: () => "finalPosition",
			name: t("finalPosition"),
			headers: [
				{
					id: (e: any) => e.final?.position?.onOrder,
					key: "finalonOrder",
					name: t("onOrder"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.final?.position?.position,
					key: "finalposition",
					name: t("position"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.final?.csl,
					key: "finalcurrentCSL",
					name: t("csl"),
					align: "right",
					noWrap: true,
					formatter: PercentageNoDecimalFormatter,
				},
				{
					id: (e: any) => e.final?.doh,
					key: "finalDays",
					name: t("doh"),
					align: "right",
					noWrap: true,
					formatter: NaturalFormatter,
				},
				{
					id: (e: any) => e.final?.expectedSales,
					key: "expectedSalesFinal",
					name: t("expectedSales"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
				},
				{
					id: (e: any) => e.plans?.reduce((a: number, b: any) => a + (b.final?.eus || 0), 0),
					key: "finalRisk",
					name: t("risk"),
					align: "right",
					noWrap: true,
					formatter: DecimalFormatter,
					child: {
						id: (e: any) => e.final?.eus,
						key: "finalRiskChild",
						name: t("risk"),
						align: "right",
						noWrap: true,
						formatter: DecimalFormatter,
					},
				},
			],
		},

	] as SBTableColumn<any>[], [onOpenInventory, t]);

	return { headers };
};
